import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useTranslation } from "react-i18next";
import { useGetHomeStats } from "../../api-data/service-provider/home-stats";

// ---------------------------------------------------------------------------
// CountUp — animates a number from 0 to `to` when it scrolls into view.
// ---------------------------------------------------------------------------
const CountUp: React.FC<{ to: number; suffix?: string; duration?: number }> = ({
  to,
  suffix = "",
  duration = 1400,
}) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const run = () => {
      if (started.current) return;
      started.current = true;
      let raf: number;
      let start: number | undefined;
      const step = (t: number) => {
        if (!start) start = t;
        const progress = Math.min((t - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(eased * to);
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
    };

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && run()),
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [to, duration]);

  const display =
    to >= 1000
      ? `${(value / 1000).toFixed(1).replace(/\.0$/, "")}K`
      : String(Math.round(value));

  return <span ref={ref}>{display}{suffix}</span>;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface GrowingStatsSectionProps {
  /** "mobile" renders a compact stacked layout; "desktop" renders the larger centred layout */
  variant: "mobile" | "desktop";
}

// ---------------------------------------------------------------------------
// GrowingStatsSection
// ---------------------------------------------------------------------------
const GrowingStatsSection: React.FC<GrowingStatsSectionProps> = ({ variant }) => {
  const { t } = useTranslation();
  const { data } = useGetHomeStats();

  if (!data) return null;

  const stats = [
    { to: data.activeCities, suffix: "", label: t("home.kpiCities") },
    { to: data.activeServiceProviders, suffix: "+", label: t("home.kpiProfessionals") },
    { to: data.totalCategories, suffix: "", label: t("home.kpiCategories") },
  ];

  if (variant === "mobile") {
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 3, mb: 1.75 }}>
          <ShieldOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: "primary.dark" }}>
            {t("home.growingTitle")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", py: 0.25 }}>
          {stats.map((stat, i, arr) => (
            <React.Fragment key={stat.label}>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 24, color: "primary.main", lineHeight: 1 }}>
                  <CountUp to={stat.to} suffix={stat.suffix} />
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", mt: 0.75 }}>
                  {stat.label}
                </Typography>
              </Box>
              {i < arr.length - 1 && <Box sx={{ width: "1px", height: 34, bgcolor: "divider" }} />}
            </React.Fragment>
          ))}
        </Box>

        <Typography sx={{ textAlign: "center", fontSize: 12.5, fontWeight: 500, color: "text.secondary", mt: 0.5, mb: 3 }}>
          {t("home.growingFootnote")}
          <TrendingUpIcon sx={{ fontSize: 15, color: "primary.main", verticalAlign: "-2px", mx: 0.6 }} />
          <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
            {t("home.comingSoon")}
          </Box>
        </Typography>
      </>
    );
  }

  // desktop variant
  return (
    <>
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <ShieldOutlinedIcon sx={{ fontSize: 22, color: "primary.main" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 24, color: "primary.dark" }}>
            {t("home.growingTitle")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, py: "24px 0 8px" }}>
        {stats.map((stat, i, arr) => (
          <React.Fragment key={stat.label}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontWeight: 800, fontSize: 44, color: "primary.main", lineHeight: 1 }}>
                <CountUp to={stat.to} suffix={stat.suffix} />
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: "text.secondary", mt: 1 }}>
                {stat.label}
              </Typography>
            </Box>
            {i < arr.length - 1 && <Box sx={{ width: "1px", height: 48, bgcolor: "divider" }} />}
          </React.Fragment>
        ))}
      </Box>

      <Typography sx={{ textAlign: "center", fontSize: 14.5, fontWeight: 500, color: "text.secondary", mt: 1.5, mb: 1 }}>
        {t("home.growingFootnote")}
        <TrendingUpIcon sx={{ fontSize: 16, color: "primary.main", verticalAlign: "-2px", mx: 0.75 }} />
        <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
          {t("home.comingSoon")}
        </Box>
      </Typography>
    </>
  );
};

export default GrowingStatsSection;
